/**
 * Public, read-only document views behind emailed links (tracker item 35):
 * /view/invoice/:token and /view/statement/:token. Outside the app's auth and
 * nav; the API returns a minimal DTO (letterhead, document, totals) and the
 * same 404 for unknown, expired or revoked links. Laid out like the printable
 * documents (item 26: print-sheet styling and the same letterhead helpers),
 * with a Print button. The invoice page has Pay now (item 38, PublicPayPanel).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { createAPIClient, INotificationsApi } from '../../api';
import * as types from '../../api/types';
import { primaryButtonClass } from '../../components/formStyles';
import { invoiceDate, invoiceMoney } from '../invoiceUi';
import { companyAddressLines, companyContactLine } from '../print/printUi';
import { PublicPayPanel } from './PublicPayPanel';

function usePublicDocument<T>(token: string, load: (api: INotificationsApi, token: string) => Promise<T>, reloadKey = 0) {
  const api: INotificationsApi = useMemo(() => createAPIClient(), []);
  const [state, setState] = useState<{ data: T | null; loading: boolean; failed: boolean }>({ data: null, loading: true, failed: false });
  useEffect(() => {
    let active = true;
    load(api, token)
      .then((data) => { if (active) setState({ data, loading: false, failed: false }); })
      .catch(() => { if (active) setState({ data: null, loading: false, failed: true }); });
    return () => { active = false; };
    // load is a stable module-level function per page
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, token, reloadKey]);
  return state;
}

export function PublicLayout({ title, company, children }: { title: string; company: types.PublicLetterhead; children: React.ReactNode }) {
  useEffect(() => {
    const previous = document.title;
    document.title = title;
    return () => { document.title = previous; };
  }, [title]);
  const addressLines = companyAddressLines(company);
  const contact = companyContactLine(company);
  return (
    <div className="print-root min-h-screen bg-gray-100 pb-10">
      <div className="print:hidden border-b border-gray-200 bg-white">
        <div className="mx-auto flex max-w-[8.5in] justify-end px-4 py-3">
          <button type="button" className={`${primaryButtonClass} min-h-[44px] sm:min-h-0`} onClick={() => window.print()}>Print</button>
        </div>
      </div>
      <article className="print-sheet mx-3 mt-4 sm:mx-auto sm:mt-6 max-w-[8.5in] rounded-lg sm:rounded-none bg-white px-4 sm:px-10 py-6 sm:py-10 text-sm text-gray-900 shadow">
        <header className="print-keep mb-6 sm:mb-8 flex flex-col sm:flex-row items-start gap-4 border-b border-gray-300 pb-6">
          {company.logoUrl && <img src={company.logoUrl} alt={`${company.companyName} logo`} className="max-h-20 max-w-[10rem] object-contain" />}
          <div className="min-w-0 break-words">
            <p className="text-lg font-bold">{company.companyName}</p>
            {addressLines.map((line) => <p key={line}>{line}</p>)}
            {contact && <p>{contact}</p>}
          </div>
        </header>
        {children}
        {company.footerNote && <footer className="print-keep mt-10 border-t border-gray-300 pt-4 text-xs whitespace-pre-wrap break-words text-gray-700">{company.footerNote}</footer>}
      </article>
    </div>
  );
}

export function Unavailable({ loading }: { loading: boolean }) {
  return (
    <main className="min-h-screen bg-gray-100 flex items-center justify-center px-4">
      {loading ? (
        <p role="status" className="text-gray-600">Loading…</p>
      ) : (
        <div className="max-w-md rounded-lg bg-white p-6 shadow text-center">
          <h1 className="text-lg font-semibold text-gray-900">This link is not available</h1>
          <p className="mt-2 text-sm text-gray-600">It may have expired or been replaced. Please contact the sender for a new link.</p>
        </div>
      )}
    </main>
  );
}

const cell = 'px-2 py-2 align-top';
const num = `${cell} text-right whitespace-nowrap`;

export function PublicInvoicePage() {
  const { token = '' } = useParams<{ token: string }>();
  const [reloadKey, setReloadKey] = useState(0);
  const { data, loading } = usePublicDocument(token, (api, t) => api.getPublicInvoiceView(t), reloadKey);
  if (!data) return <Unavailable loading={loading} />;
  const hasDiscounts = Number(data.totalDiscount) > 0;
  const headings = ['Description', 'Qty', 'Unit price', 'Amount', ...(hasDiscounts ? ['Discount'] : []), 'Tax'];
  return (
    <PublicLayout title={`Invoice ${data.invoiceNumber} - ${data.company.companyName}`} company={data.company}>
      <section className="print-keep mb-6 sm:mb-8 flex flex-wrap items-start justify-between gap-4 sm:gap-6">
        <div className="min-w-0 break-words">
          <h1 className="text-2xl font-bold tracking-wide">INVOICE</h1>
          <p className="mt-1 text-base font-semibold">#{data.invoiceNumber}</p>
          <p className="mt-3"><span className="text-gray-600">Bill to </span><span className="font-semibold">{data.customerName}</span></p>
        </div>
        <dl className="grid grid-cols-[auto_auto] gap-x-4 gap-y-1">
          <dt className="text-gray-600">Invoice date</dt><dd>{invoiceDate(data.invoiceDate)}</dd>
          <dt className="text-gray-600">Due date</dt><dd>{invoiceDate(data.dueDate)}</dd>
          {data.periodStartDate && <><dt className="text-gray-600">Service period</dt><dd>{invoiceDate(data.periodStartDate)} to {invoiceDate(data.periodEndDate ?? undefined)}</dd></>}
        </dl>
      </section>
      <div className="overflow-x-auto">
        <table className="print-table responsive-table w-full border-collapse text-sm" aria-label="Invoice line items">
          <thead>
            <tr className="border-b-2 border-gray-800">
              {headings.map((h, i) => <th key={h} className={`${i === 0 ? 'text-left' : 'text-right'} px-2 py-2 font-semibold`}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {data.lines.map((line, index) => (
              <tr key={index} className="border-b border-gray-200">
                <td className={cell} data-label="Description" data-primary>{line.description}</td>
                <td className={num} data-label="Qty">{line.quantity}</td>
                <td className={num} data-label="Unit price">{invoiceMoney(line.unitPrice)}</td>
                <td className={num} data-label="Amount">{invoiceMoney(line.amount)}</td>
                {hasDiscounts && <td className={num} data-label="Discount">{Number(line.discount) ? `−${invoiceMoney(line.discount)}` : '—'}</td>}
                <td className={num} data-label="Tax">{invoiceMoney(line.tax)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <dl className="print-keep mt-6 ml-auto w-full max-w-xs space-y-1" aria-label="Invoice totals">
        {hasDiscounts && <div className="flex justify-between gap-6"><dt>Discounts</dt><dd>−{invoiceMoney(data.totalDiscount)}</dd></div>}
        <div className="flex justify-between gap-6"><dt>Tax</dt><dd>{invoiceMoney(data.totalTax)}</dd></div>
        <div className="flex justify-between gap-6"><dt>Total</dt><dd>{invoiceMoney(data.total)}</dd></div>
        <div className="flex justify-between gap-6"><dt>Paid</dt><dd>{invoiceMoney(data.amountPaid)}</dd></div>
        <div className="flex justify-between gap-6 border-t-2 border-gray-800 pt-2 text-base font-bold"><dt>Balance due</dt><dd>{invoiceMoney(data.balanceDue)}</dd></div>
      </dl>
      <PublicPayPanel token={token} onPaid={() => setReloadKey((k) => k + 1)} />
    </PublicLayout>
  );
}

export function PublicStatementPage() {
  const { token = '' } = useParams<{ token: string }>();
  const { data, loading } = usePublicDocument(token, (api, t) => api.getPublicStatementView(t));
  if (!data) return <Unavailable loading={loading} />;
  return (
    <PublicLayout title={`Statement - ${data.company.companyName}`} company={data.company}>
      <section className="print-keep mb-6 sm:mb-8 flex flex-wrap items-start justify-between gap-4 sm:gap-6">
        <div className="min-w-0 break-words">
          <h1 className="text-2xl font-bold tracking-wide">STATEMENT</h1>
          <p className="mt-3"><span className="text-gray-600">Account </span><span className="font-semibold">{data.customerName}</span></p>
        </div>
        <dl className="grid grid-cols-[auto_auto] gap-x-4 gap-y-1">
          <dt className="text-gray-600">Statement date</dt><dd>{invoiceDate(data.statementDate)}</dd>
          <dt className="text-gray-600 font-semibold">Balance due</dt><dd className="font-semibold">{invoiceMoney(data.closingBalance)}</dd>
        </dl>
      </section>
      <div className="overflow-x-auto">
        <table className="print-table responsive-table w-full border-collapse text-sm" aria-label="Statement lines">
          <thead>
            <tr className="border-b-2 border-gray-800">
              {['Date', 'Reference', 'Description', 'Charge', 'Credit', 'Balance'].map((h, i) => (
                <th key={h} className={`${i < 3 ? 'text-left' : 'text-right'} px-2 py-2 font-semibold`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.lines.length === 0 ? (
              <tr><td colSpan={6} className={`${cell} text-gray-600`}>No activity.</td></tr>
            ) : data.lines.map((line, index) => (
              <tr key={index} className="border-b border-gray-200">
                <td className={cell} data-label="Date">{invoiceDate(line.date)}</td>
                <td className={cell} data-label="Reference">{line.reference}</td>
                <td className={cell} data-label="Description">{line.description}</td>
                <td className={num} data-label="Charge">{Number(line.charge) ? invoiceMoney(line.charge) : ''}</td>
                <td className={num} data-label="Credit">{Number(line.credit) ? invoiceMoney(line.credit) : ''}</td>
                <td className={num} data-label="Balance">{invoiceMoney(line.runningBalance)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </PublicLayout>
  );
}
